/**
 * AddDeviceSheet —— 「添加设备」三步向导。
 * 1 接入方式（模拟设备 / 绑定码绑定） → 2 设备信息（绑定码方式只需输 6 位码） → 3 完成。
 * 真机只走绑定码：设备自注册后把 6 位码显示在屏幕/串口，用户输码即归属当前账号。
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Device, DeviceSpec, DeviceType } from '@znjj/shared';
import { deviceKeys, devicesApi, ROOMS } from '../api/devices';
import { Icon } from './Icon';
import { fmt, Sheet, toast } from './ui';

const TYPE_DESC: Record<DeviceType, string> = {
  light: '单路或多路开关，支持定时与语音控制',
  ac: '冷暖两用，支持温度与模式调节',
  env: 'CO₂ / TVOC / 甲醛 / 温湿度 / 气压 多合一',
  th: '温湿度监测，可联动空调 / 加湿器',
  plug: '通断 + 功率计量，可统计电器用电',
  curtain: '开合度控制，支持定时与场景联动',
  gas: '监测空气中可燃气体浓度（%LEL），默认放在厨房，超阈值立即报警',
};

/** 接入方式：平台造数的模拟设备 / 设备自己出码的绑定码方式 */
type SourceKind = 'sim' | 'pair';

const SRC_HINT: Record<SourceKind, string> = {
  sim: '平台按定时器为模拟设备造数，用于在没有真实硬件时跑通「展示 → 控制 → 历史」全流程。',
  pair: '设备固件内置产品密钥，上电后自动连上平台，并把 6 位绑定码显示在屏幕或串口日志里。在这里输入该码，设备即归属当前账号。',
};

export function AddDeviceSheet({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (device: Device) => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [src, setSrc] = useState<SourceKind>('sim');
  const [type, setType] = useState<DeviceType>('light');
  const [room, setRoom] = useState<string>('客厅');
  const [name, setName] = useState('');
  const [nameErr, setNameErr] = useState('');
  const [code, setCode] = useState('');
  const [codeErr, setCodeErr] = useState('');
  const [created, setCreated] = useState<Device | null>(null);

  const typesQuery = useQuery({
    queryKey: deviceKeys.types,
    queryFn: devicesApi.types,
    enabled: open,
    staleTime: Infinity,
  });
  const types = typesQuery.data?.list ?? [];

  const reset = (): void => {
    setStep(1);
    setSrc('sim');
    setType('light');
    setRoom('客厅');
    setName('');
    setNameErr('');
    setCode('');
    setCodeErr('');
    setCreated(null);
  };

  const submit = useMutation({
    mutationFn: async () =>
      devicesApi.create({
        name: name.trim(),
        type,
        room,
        source: 'sim',
      }),
    onSuccess: ({ device }) => {
      setCreated(device);
      setStep(3);
      void qc.invalidateQueries({ queryKey: deviceKeys.list });
    },
    onError: (err: Error) => toast.err(err.message),
  });

  /** 绑定码认领：设备已在平台上等认领，输码成功后它立刻出现在设备墙 */
  const pair = useMutation({
    mutationFn: (c: string) => devicesApi.pair(c),
    onSuccess: ({ device }) => {
      setCreated(device);
      setStep(3);
      void qc.invalidateQueries({ queryKey: deviceKeys.list });
    },
    onError: (err: Error) => setCodeErr(err.message),
  });

  const next = (): void => {
    if (step === 1) {
      setStep(2);
      return;
    }
    if (step === 2) {
      if (src === 'pair') {
        if (!/^\d{6}$/.test(code)) {
          setCodeErr('请输入设备屏幕/串口上的 6 位绑定码');
          return;
        }
        pair.mutate(code);
        return;
      }
      if (name.trim().length < 2) {
        setNameErr('设备名称至少 2 个字');
        return;
      }
      submit.mutate();
      return;
    }
    if (created) onCreated(created);
    onClose();
    reset();
  };

  return (
    <Sheet open={open} onClose={onClose} title="添加设备">
      <div className="row row--wrap" style={{ marginBottom: 16 }}>
        {[1, 2, 3].map((s, i) => (
          <span key={s} className="row" style={{ gap: 6 }}>
            {i > 0 ? <Icon name="chevron-right" size={12} style={{ color: 'var(--color-ink-dim)' }} /> : null}
            <span className={'tag' + (step === s ? ' tag--primary' : '')}>{s} {['接入方式', src === 'pair' ? '绑定码' : '设备信息', '完成'][i]}</span>
          </span>
        ))}
      </div>

      {step === 1 ? (
        <div>
          <div className="field__label" style={{ marginBottom: 8 }}>
            接入方式
          </div>
          <div className="seg" style={{ width: '100%' }}>
            {(
              [
                ['sim', '模拟设备'],
                ['pair', '绑定码绑定'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                className={'seg__btn' + (src === value ? ' is-active' : '')}
                type="button"
                style={{ flex: 1 }}
                onClick={() => setSrc(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="field__hint" style={{ marginTop: 10, lineHeight: 1.7 }}>
            {SRC_HINT[src]}
          </p>
        </div>
      ) : null}

      {step === 2 && src === 'pair' ? (
        <div>
          <div className="field__label" style={{ marginBottom: 8 }}>
            绑定码
          </div>
          <input
            className={'input' + (codeErr ? ' input--err' : '')}
            value={code}
            inputMode="numeric"
            maxLength={6}
            placeholder="6 位数字"
            style={{ letterSpacing: '0.4em', fontSize: 20, textAlign: 'center' }}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
              setCodeErr('');
            }}
          />
          {codeErr ? <span className="field__err">{codeErr}</span> : null}
          <p className="field__hint" style={{ marginTop: 10, lineHeight: 1.7 }}>
            设备上电后会连上平台，并把绑定码显示在屏幕或串口日志里。绑定成功后设备名称默认取类型名，可随时在设备详情里改。
          </p>
        </div>
      ) : null}

      {step === 2 && src !== 'pair' ? (
        <div>
          <div className="field__label" style={{ marginBottom: 8 }}>
            设备类型
          </div>
          <div className="stack" style={{ gap: 8 }}>
            {types.map((t: DeviceSpec) => (
              <button
                key={t.type}
                className={'typepick' + (type === t.type ? ' is-on' : '')}
                type="button"
                onClick={() => {
                  setType(t.type);
                  // 燃气报警器装在厨房，选中时把房间默认值一并切过去
                  if (t.type === 'gas') setRoom('厨房');
                }}
              >
                <span className="typepick__ico">
                  <Icon name={t.icon} size={16} />
                </span>
                <span style={{ minWidth: 0 }}>
                  <span className="typepick__name">{t.label}</span>
                  <span className="typepick__desc">{TYPE_DESC[t.type]}</span>
                </span>
              </button>
            ))}
          </div>

          <div className="field" style={{ marginTop: 14 }}>
            <span className="field__label">设备名称</span>
            <input
              className={'input' + (nameErr ? ' input--err' : '')}
              value={name}
              maxLength={20}
              placeholder="如：书房台灯、主卧空调"
              onChange={(e) => {
                setName(e.target.value);
                setNameErr('');
              }}
            />
            {nameErr ? <span className="field__err">{nameErr}</span> : null}
          </div>

          <div style={{ marginTop: 14 }}>
            <div className="field__label" style={{ marginBottom: 7 }}>
              所属房间
            </div>
            <div className="row row--wrap">
              {ROOMS.map((r) => (
                <button
                  key={r}
                  className={'chip' + (room === r ? ' is-on' : '')}
                  type="button"
                  onClick={() => setRoom(r)}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {step === 3 && created ? (
        <div>
          <div className="card card--flat">
            <div className="card__head" style={{ marginBottom: 8 }}>
              <div className="card__title">
                <Icon name={created.spec.icon} size={16} />
                {created.name}
              </div>
              <span className="tag tag--on">
                <i className="dot dot--on" />
                {src === 'pair' ? '已绑定' : '已创建'}
              </span>
            </div>
            <div className="row row--wrap t-sm muted" style={{ gap: 8 }}>
              <span className="tag">{created.spec.label}</span>
              {created.room ? <span className="tag">{created.room}</span> : null}
              <span className="tag">{fmt.source(created.source)}</span>
              <span className="tag">设备编号 {created.id}</span>
            </div>
          </div>

          <p className="field__hint" style={{ marginTop: 12, lineHeight: 1.7 }}>
            {src === 'pair'
              ? '设备凭据由设备自身持有（自注册时已下发，无需在此抄录）；它继续上报后，卡片就会显示实时读数。'
              : '模拟设备已开始按定时器造数，卡片会显示实时读数。'}
          </p>
        </div>
      ) : null}

      <div className="btn-row" style={{ marginTop: 18 }}>
        <button
          className="btn btn--ghost"
          type="button"
          disabled={step === 1 || submit.isPending}
          onClick={() => setStep((s) => Math.max(1, s - 1))}
        >
          上一步
        </button>
        <button
          className="btn btn--primary"
          type="button"
          disabled={submit.isPending || pair.isPending}
          onClick={next}
        >
          {submit.isPending || pair.isPending ? '处理中…' : step === 3 ? '完成' : '下一步'}
        </button>
      </div>
    </Sheet>
  );
}
